/**
 * GlobalSearch — Cmd+K spotlight for products, categories, orders,
 * insights and opportunities. Consumes /api/search.
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Package, Boxes, Receipt, Sparkles, Target, X } from "lucide-react";
import { api } from "../../lib/api";

const ICONS = {
  product: Package, category: Boxes, order: Receipt,
  insight: Sparkles, opportunity: Target,
};

export default function GlobalSearch({ open, onClose }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 30); }, [open]);
  useEffect(() => { if (!open) { setQ(""); setItems([]); } }, [open]);

  useEffect(() => {
    let ignore = false;
    if (!q || q.length < 1) { setItems([]); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q)}&limit=6`);
        if (!ignore) setItems(data.results || []);
      } finally { if (!ignore) setLoading(false); }
    }, 180);
    return () => { ignore = true; clearTimeout(t); };
  }, [q]);

  const go = (r) => { onClose(); navigate(r.route); };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center pt-24" onClick={onClose} data-testid="global-search-overlay">
      <div className="w-[min(640px,90vw)] bg-zinc-950 border border-zinc-800 rounded-2xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-4 py-3 border-b border-zinc-800">
          <Search className="h-4 w-4 text-zinc-500" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search products, categories, orders, insights, opportunities…"
            className="bg-transparent flex-1 outline-none text-sm text-zinc-100 placeholder-zinc-600"
            data-testid="global-search-input"
          />
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300" data-testid="global-search-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {loading && <p className="px-4 py-6 text-xs text-zinc-500">Searching…</p>}
          {!loading && q && items.length === 0 && <p className="px-4 py-6 text-xs text-zinc-500">No matches.</p>}
          {!loading && !q && <p className="px-4 py-6 text-xs text-zinc-500">Type to search across your workspace.</p>}
          {items.map((r) => {
            const Icon = ICONS[r.type] || Search;
            return (
              <button
                key={`${r.type}-${r.id}`}
                onClick={() => go(r)}
                className="w-full text-left flex items-start gap-3 px-4 py-3 hover:bg-zinc-900 transition-colors"
                data-testid={`search-result-${r.type}-${r.id}`}
              >
                <Icon className="h-4 w-4 text-zinc-500 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-zinc-100 truncate">{r.title}</p>
                  <p className="text-xs text-zinc-500 truncate">{r.subtitle}</p>
                </div>
                <span className="text-[10px] uppercase tracking-[0.14em] text-zinc-600">{r.type}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
